import React, { ReactNode, useCallback } from "react";
import { logError } from "utils/logger";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { useNotifications } from "modules/notification";
import { createOrUpdateQuoteDTOSchema } from "../../model/schema";
import { ConfigurationRenderProps, QuoteFormValues } from "../../types";
import { CREATE_OR_UPDATE_QUOTE, GET_QUOTE_BY_VO_ID } from "../../api";
import {
  CreateOrUpdateQuoteResponse,
  CreateOrUpdateQuoteVariables,
  GetQuoteResponse,
  GetQuoteVariables,
} from "../../api/types";

interface Props {
  voId: string;
  onSuccess?: VoidFunction;
  children: (props: ConfigurationRenderProps) => ReactNode;
}

export const QuoteAPIContainer = ({ voId, children, onSuccess }: Props) => {
  const { showNotification } = useNotifications();

  const {
    networkStatus,
    data: quoteResponse,
    refetch: fetchQuote,
  } = useQuery<GetQuoteResponse, GetQuoteVariables>(GET_QUOTE_BY_VO_ID, {
    skip: !voId,
    notifyOnNetworkStatusChange: true,
    variables: { vendorOpportunityId: voId },
  });

  const [onCreateOrUpdate, { loading: isQuoteUpdating }] = useMutation<
    CreateOrUpdateQuoteResponse,
    CreateOrUpdateQuoteVariables
  >(CREATE_OR_UPDATE_QUOTE);

  const handleCreateOrUpdateQuote = useCallback(
    async (dto: QuoteFormValues) => {
      try {
        const collectCreateOrUpdateDTO: CreateOrUpdateQuoteVariables["input"] = { vendorOpportunityId: voId, ...dto };

        const { success, error, data } = createOrUpdateQuoteDTOSchema.safeParse(collectCreateOrUpdateDTO);

        if (success) {
          await onCreateOrUpdate({ variables: { input: data } });
          await fetchQuote();
          onSuccess?.();
          showNotification("Successfully updated quote");
        } else {
          logError("handleUpdateQuote input parse failed", error);
        }
      } catch (error) {
        logError(error);
        showNotification("Failed update quote", { type: "error" });
      }
    },
    [onSuccess, onCreateOrUpdate, showNotification, voId, fetchQuote]
  );

  return (
    <>
      {children({
        handleCreateOrUpdateQuote,
        isFetching: networkStatus === NetworkStatus.loading,
        quote: quoteResponse?.vendorOpportunity?.quote ?? null,
        isLoading: networkStatus === NetworkStatus.refetch || isQuoteUpdating,
      })}
    </>
  );
};
